import Api from '@/services/Api';
import helpers from '@/js/helpers';

export default {
  namespaced: true,
  state: {
    confirmPopup: false,
    showSplPopup: false,
    currency: 'EUR', // Almacena la divisa del cliente seleccionado
    issuedInvoiceList: [], // Almacena la lista de facturas
    issuedInvoiceSelected: {}, // Almacena el código de la factura seleccionada
    issuedInvoiceDetails: {}, // Almacena los detalles de la factura seleccionada
    issuedInvoiceCodeInfoPopUp: [],
    isLoading: false,
    firstLoadDone: false,
  },
  actions: {
    async fetchIssuedInvoiceList(
      { commit, rootState },
      { fechaInicio, fechaFin }
    ) {
      try {
        commit('setLoading', true);
        commit('setIssuedInvoiceList', []);
        const xhr = await Api.getIssuedInvoiceList(
          rootState.contact.company,
          fechaInicio,
          fechaFin
        );
        const response = JSON.parse(xhr.response);
        const facturas = (response.facturas || []).map((f) => ({
          ...f,
          FechaFacturaDate: helpers.toDateFromYyyymmdd(
            f.FechaFactura
          ),
        }));
        commit('setIssuedInvoiceList', facturas);
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      } finally {
        commit('setLoading', false);
        commit('setFirstLoadDone', true);
      }
    },
    async fetchIssuedInvoicePDF({ commit }, codFactura) {
      try {
        commit('setLoading', true);
        const xhr = await Api.getIssuedInvoicePDF(codFactura);
        const response = JSON.parse(xhr.response); // { CodFactura, DocumentoPDF }
        const base64 = response?.DocumentoPDF;
        if (!base64)
          throw new Error('PDF vacío o respuesta inválida');
        return base64; // devolvemos el string base64
      } finally {
        commit('setLoading', false);
      }
    },
    async fetchIssuedInvoiceDetails(
      { commit, state, rootState },
      codFactura
    ) {
      try {
        const divisa =
          state.issuedInvoiceSelected?.Divisa ||
          rootState.contact.addresses?.[0]?.currency ||
          'EUR';
        commit('setCurrency', divisa);

        const xhr = await Api.getIssuedInvoiceDetails(codFactura);
        const response = JSON.parse(xhr.response); // { CodFactura, LineasFactura: [...] }

        const sel = state.issuedInvoiceSelected || {};

        const CodFactura =
          sel.CodFactura || response.CodFactura || codFactura || '';
        const FechaFactura = sel.FechaFactura || '';
        const OrdenCompra = sel.OrdenCompra ?? '';
        const ImporteBase =
          typeof sel.ImporteBase === 'number'
            ? sel.ImporteBase
            : Number(sel.ImporteBase) || 0;
        const ImporteImpuestos =
          typeof sel.ImporteImpuestos === 'number'
            ? sel.ImporteImpuestos
            : Number(sel.ImporteImpuestos) || 0;
        const ImporteTotal =
          typeof sel.Importe === 'number'
            ? sel.Importe
            : Number(sel.Importe) || 0;
        const Divisa = sel.Divisa || divisa;
        const Estado = sel.Estado ?? '';

        const groups = {};
        (response.LineasFactura || []).forEach((l) => {
          const key = l.CodArticulo || 'SIN_COD';
          if (!groups[key]) {
            groups[key] = {
              CodArticulo: key,
              Articulo: l.Articulo || '',
              Unidades: Number(l.Cantidad || 0),
              preciosBaseSet: new Set(),
              totalAcumulado: Number(l.PrecioTotal || 0),
              tasaImpuestos: Number(l.TasaImpuestos || 0),
              Observaciones: l.Observaciones || '',
              CodPresupuesto: l.CodPresupuesto || '',
              Muestras: [],
            };
          }

          if (l.PrecioBase != null)
            groups[key].preciosBaseSet.add(String(l.PrecioBase));

          groups[key].Muestras.push({
            CodMuestra: l.CodMuestra || '',
            Muestra: l.Muestra || '',
            IdTipoMuestra: l.idTipoMuestra || null,
            FechaMuestra:
              helpers.toISOFromYyyymmdd(
                l.FechaMuestra?.slice(0, 10)
              ) || null,
            TipoMuestra: l.TipoMuestra || '',
          });
        });

        const LineasPorArticulo = Object.values(groups).map((g) => {
          const out = {
            CodArticulo: g.CodArticulo,
            Articulo: g.Articulo,
            Unidades: g.Unidades,
            Total: Number(g.totalAcumulado),
            TasaImpuestos: Number(g.tasaImpuestos ?? '-'),
            Observaciones: g.Observaciones,
            CodPresupuesto: g.CodPresupuesto,
            Muestras: g.Muestras,
          };
          if (g.preciosBaseSet.size === 1) {
            out.PrecioBase = Number([...g.preciosBaseSet][0]);
          }
          return out;
        });

        const vm = {
          CodFactura,
          FechaFactura,
          FechaFacturaDate: helpers.toDateFromYyyymmdd(FechaFactura),
          OrdenCompra,
          ImporteBase,
          ImporteImpuestos,
          ImporteTotal,
          Divisa,
          Estado,
          LineasPorArticulo,
        };

        commit('setIssuedInvoiceDetails', vm);
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    setConfirmPopup({ commit }, value) {
      commit('setConfirmPopup', value);
    },
    setSplPopup({ commit }, value) {
      commit('setSplPopup', value);
    },
    setCurrency({ commit }, value) {
      commit('setCurrency', value);
    },
    setIssuedInvoiceList({ commit }, value) {
      commit('setIssuedInvoiceList', value);
    },
    setIssuedInvoiceSelected({ commit }, value) {
      commit('setIssuedInvoiceSelected', value);
    },
    setIssuedInvoiceDetails({ commit }, value) {
      commit('setIssuedInvoiceDetails', value);
    },
    setIssuedInvoiceCodeInfoPopUp({ commit }, value) {
      commit('setIssuedInvoiceCodeInfoPopUp', value);
    },
    resetData({ commit }) {
      commit('resetData');
    },
  },
  mutations: {
    setConfirmPopup(state, value) {
      state.confirmPopup = value;
    },
    setSplPopup(state, value) {
      state.showSplPopup = value;
    },
    setCurrency(state, value) {
      state.currency = value;
    },
    setIssuedInvoiceList(state, value) {
      state.issuedInvoiceList = value;
    },
    setIssuedInvoiceSelected(state, value) {
      state.issuedInvoiceSelected = value;
    },
    setIssuedInvoiceDetails(state, value) {
      state.issuedInvoiceDetails = value;
    },
    setIssuedInvoiceCodeInfoPopUp(state, value) {
      state.issuedInvoiceCodeInfoPopUp = value;
    },
    setLoading(state, v) {
      state.isLoading = !!v;
    },
    resetData(state) {
      state.confirmPopup = false;
      state.showSplPopup = false;
      state.issuedInvoiceList = [];
      state.issuedInvoiceSelected = '';
      state.issuedInvoiceDetails = [];
      state.issuedInvoiceCodeInfoPopUp = [];
    },
    setFirstLoadDone(state, value) {
      state.firstLoadDone = value;
    },
  },
};
